import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TenantType } from 'src/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSuccessUploadingTenantLogo,
  selectUploadingTenantLogo,
  selectSuccessDeletingTenantLogo,
  selectDeletingTenantLogo,
} from 'src/redux/tenants/selectors';
import { deleteTenantLogo, uploadTenantLogo } from 'src/redux/tenants/actions';
import { useTenant } from 'src/api/tenant';
import ImageUploadLayout from 'src/components/ImageUploadLayout';

type Props = {
  tenant: TenantType;
  onClose: () => void;
};

const UploadTenantLogoModal = ({ tenant, onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [slideValue, setSlideValue] = useState(10);
  const [preview, setPreview] = useState('');
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const cropRef = useRef(null);
  const { refetch } = useTenant(tenant.id);
  const uploadTenantLogoSuccess = useSelector(selectSuccessUploadingTenantLogo);
  const uploadingTenantLogo = useSelector(selectUploadingTenantLogo);
  const deleteTenantLogoSuccess = useSelector(selectSuccessDeletingTenantLogo);
  const deletingTenantLogo = useSelector(selectDeletingTenantLogo);

  const handleSave = async () => {
    if (cropRef) {
      // @ts-ignore
      const dataUrl = cropRef?.current.getImage().toDataURL();
      const result = await fetch(dataUrl);
      const blob = await result.blob();
      dispatch(uploadTenantLogo.init({ tenantId: tenant.id, file: blob }));
    }
  };

  const handleDeleteImg = () => {
    dispatch(deleteTenantLogo.init({ tenantId: tenant.id }));
  };

  useEffect(() => {
    if (uploadTenantLogoSuccess) {
      dispatch(uploadTenantLogo.reset());
      refetch();
      setIsEditMode(false);
      onClose();
    }
    if (deleteTenantLogoSuccess) {
      dispatch(deleteTenantLogo.reset());
      refetch();
      setIsEditMode(false);
      setShowDeletePopup(false);
      onClose();
    }
  }, [uploadTenantLogoSuccess, deleteTenantLogoSuccess]);

  useEffect(() => {
    if (tenant.logoUrl) {
      setPreview(tenant.logoUrl);
    }
  });

  return (
    <ImageUploadLayout
      onClose={onClose}
      imageName={t('companyPage.logo')}
      preview={preview}
      slideValue={slideValue}
      cropRef={cropRef}
      displayName={tenant.title}
      handleSave={handleSave}
      setShowDeletePopup={setShowDeletePopup}
      setSlideValue={setSlideValue}
      uploadingImage={uploadingTenantLogo}
      deletingImage={deletingTenantLogo}
      showDeletePopup={showDeletePopup}
      handleDeleteImg={handleDeleteImg}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      deleteImgSubTitle={t('companyPage.deleteImgSubTitle')}
      deleteImgTitle={t('companyPage.deleteImgTitle')}
      deleteText={t('companyPage.deleteLogo')}
      changeText={t('companyPage.changeLogo')}
      uploadText={t('companyPage.uploadLogo')}
    />
  );
};

export default UploadTenantLogoModal;
